import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: {
      title: 'Login - E-Stocks Monitor',
      requiresAuth: false
    }
  },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: {
      title: 'Dashboard - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/tickers',
    name: 'Tickers',
    component: () => import('@/views/Tickers.vue'),
    meta: {
      title: 'Tickers - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/alerts',
    name: 'Alerts',
    component: () => import('@/views/Alerts.vue'),
    meta: {
      title: 'Alerts - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    // Hidden from the main menu, but the route (and its backend API) remain:
    // reachable directly at /simulation.
    path: '/simulation',
    name: 'Simulation',
    component: () => import('@/views/Simulation.vue'),
    meta: {
      title: 'Alert Simulation - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/strategies',
    name: 'Strategies',
    component: () => import('@/views/Strategies.vue'),
    meta: {
      title: 'Custom Strategies - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/orders',
    name: 'Orders',
    component: () => import('@/views/Orders.vue'),
    meta: {
      title: 'Orders - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/positions',
    name: 'Positions',
    component: () => import('@/views/Positions.vue'),
    meta: {
      title: 'Positions - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/manual-order',
    name: 'ManualOrder',
    component: () => import('@/views/ManualOrder.vue'),
    meta: {
      title: 'Manual Order - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/Settings.vue'),
    meta: {
      title: 'Settings - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/etrade-auth',
    name: 'ETradeAuth',
    component: () => import('@/views/ETradeAuth.vue'),
    meta: {
      title: 'E*TRADE Authentication - E-Stocks Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: 'Page Not Found - E-Stocks Monitor'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Global navigation guard for authentication and page titles
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Set page title
  if (to.meta?.title) {
    document.title = to.meta.title as string
  }

  // Check authentication
  const requiresAuth = to.meta?.requiresAuth !== false // Default to true

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with return URL
    next({
      name: 'Login',
      query: { redirect: to.fullPath }
    })
    return
  }

  if (to.name === 'Login' && authStore.isAuthenticated) {
    // Already logged in, redirect to dashboard
    next({ name: 'Dashboard' })
    return
  }

  // Hide the E*TRADE Auth page entirely when a different broker is active
  if (to.name === 'ETradeAuth') {
    const { useBrokerStore } = await import('@/stores/broker')
    const brokerStore = useBrokerStore()
    if (!brokerStore.loaded) {
      await brokerStore.load()
    }
    if (!brokerStore.isEtrade) {
      next({ name: 'Dashboard' })
      return
    }
  }

  next()
})

export default router
